import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDarkBg = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-black/35 backdrop-blur-sm'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            Mulebuy Spreadsheets
          </Link>

          <div className="hidden md:flex items-center gap-3 lg:gap-5">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              <i className="ri-home-4-line mr-1"></i>
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'border-background-300 text-foreground-700 hover:border-foreground-400'
                    : 'border-white/40 text-white hover:border-white/70'
                }`}
              >
                <i className="ri-shopping-bag-3-line"></i>
                Shop
                <i className={`text-xs ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200 shadow-xl py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/mulebuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg ${
              scrolled ? 'text-foreground-900 hover:bg-background-200' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-xl border border-background-200 shadow-xl p-4 mb-3 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-background-200">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-50 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <Link
                to="/mulebuy-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary-100 text-secondary-800 text-sm font-semibold hover:bg-secondary-200 transition-colors"
              >
                <i className="ri-book-open-line"></i>
                Shopping Guide
              </Link>
            </div>

            <p className="text-xs font-semibold text-foreground-500 uppercase mb-3 flex items-center gap-1.5">
              <i className="ri-shopping-bag-3-line"></i>
              Shop Categories
            </p>
            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-foreground-700 hover:bg-secondary-100 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="flex flex-col gap-2 pt-3 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line mr-1"></i>
                Mulebuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 rounded-full bg-accent-500 text-white text-sm font-semibold text-center hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-2-line mr-1"></i>
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}